import React, { useMemo, useState, useEffect } from 'react';
import { Trash, ShoppingCart, Package, ArrowRight } from '@phosphor-icons/react';
import { computeBundleTotals } from '../lib/bundleHelpers';
import TermsAcceptanceLabel from './TermsAcceptanceLabel';
import OrderTotalsPopover from './OrderTotalsPopover';

// Self-contained side panel that renders the cart's loose items + included Savers
// and the combined order subtotal. The parent owns the cart state + all mutations.
const CartPanel = ({
  cart,
  loading,
  onUpdateLooseQty,
  onRemoveLooseItem,
  onRemoveSaver,
  onSubmit,
  onClear,
  onApplySmartSaver,
  isCustomer = false,
  smartSaverHidden = false,
  busy = false,
  submitStatus = null,
}) => {
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [prepayRequested, setPrepayRequested] = useState(false);
  const [customerReference, setCustomerReference] = useState('');
  const [prepayConfig, setPrepayConfig] = useState({ enabled: true, label: '' });

  // Fetch the prepay checkbox config for THIS customer — now a per-customer
  // setting, sourced via /users/{id}/effective-config. Customers can fetch
  // their own config; admins/sales-reps fetch the targeted customer's.
  useEffect(() => {
    const customerId = cart?.customerId;
    if (!customerId) return;
    let cancelled = false;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/users/${customerId}/effective-config`;
    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setPrepayConfig({
          enabled: data.prepayEnabled !== false,
          label: data.prepayLabel || '',
        });
      })
      .catch(() => { /* silently ignore — defaults remain */ });
    return () => { cancelled = true; };
  }, [cart?.customerId]);

  // Reset the T&Cs + prepay acceptance every time the confirm panel is closed
  // so users re-affirm on each submit (audit + UX consistency with SavedBundlesList).
  useEffect(() => {
    if (!confirmingSubmit) {
      setAcceptedTerms(false);
      setPrepayRequested(false);
    }
  }, [confirmingSubmit]);

  const looseItems = cart?.looseItems || [];
  const savers = cart?.savers || [];

  const looseSubtotal = useMemo(
    () => looseItems.reduce((sum, it) => sum + (Math.round(Number(it.unitPriceSnapshot || it.wholesalePrice || 0) * 100) / 100) * Number(it.qty || 0), 0),
    [looseItems],
  );

  const saversTotals = useMemo(() => {
    let subtotal = 0;
    let saverPrice = 0;
    for (const b of savers) {
      const t = computeBundleTotals(b);
      subtotal += t.subtotal;
      saverPrice += t.bundlePrice;
    }
    return { subtotal, saverPrice };
  }, [savers]);

  const grandTotal = looseSubtotal + saversTotals.saverPrice;
  // Banked prepay credit — applied to this order's pre-GST total. Comes from the
  // backend (recomputed on every cart fetch so it tracks live as items change).
  // We re-cap it client-side against the currently-rendered grand total in case
  // the user has just added/removed an item but the parent hasn't refetched yet.
  const rawBanked = Number(cart?.prepayBankedDiscount || 0);
  const appliedBanked = Math.max(0, Math.min(rawBanked, grandTotal));
  const finalTotal = Math.max(0, grandTotal - appliedBanked);
  const isEmpty = looseItems.length === 0 && savers.length === 0;

  return (
    <div
      className="bg-surface border border-border-color rounded-xl p-5 sticky top-6"
      data-testid="cart-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold font-heading text-gold flex items-center gap-2">
          <ShoppingCart size={20} weight="duotone" />
          Cart
        </h2>
        <div className="flex items-center gap-3">
          {cart?.customerId && <OrderTotalsPopover customerId={cart.customerId} cart={cart} />}
          {!isEmpty && (
            <button
              onClick={onClear}
              disabled={busy}
              data-testid="cart-clear-button"
              className="text-xs text-text-secondary hover:text-danger disabled:opacity-50"
            >
              Clear cart
            </button>
          )}
        </div>
      </div>

      {loading && <p className="text-sm text-text-secondary italic">Loading cart…</p>}

      {!loading && isEmpty && (
        <p className="text-sm text-text-secondary italic" data-testid="cart-empty">
          {smartSaverHidden
            ? 'Cart is empty. Add catalogue items to stage them here.'
            : 'Cart is empty. Add catalogue items or save a Saver to stage them here.'}
        </p>
      )}

      {!loading && looseItems.length > 0 && onApplySmartSaver && (
        <button
          type="button"
          onClick={onApplySmartSaver}
          disabled={busy}
          data-testid="apply-smart-saver-button"
          className="w-full mb-4 px-4 py-2.5 text-sm font-semibold bg-gold/20 text-gold border border-gold/40 rounded-lg hover:bg-gold/30 hover:border-gold disabled:opacity-50 transition flex items-center justify-center gap-2"
          title="Group cart loose items into the best-saving combination of Savers"
        >
          <Package size={16} weight="bold" />
          Apply Smart Saver — preview grouping
        </button>
      )}

      {!loading && looseItems.length > 0 && (
        <div className="mb-4">
          <p className="text-xs uppercase tracking-wider text-text-secondary mb-2">
            Loose Items ({looseItems.length})
          </p>
          <ul className="space-y-2" data-testid="cart-loose-items">
            {looseItems.map((it) => {
              const unit = Math.round(Number(it.unitPriceSnapshot || it.wholesalePrice || 0) * 100) / 100;
              return (
                <li
                  key={it.lineId}
                  className="flex items-start gap-2 p-2 bg-black/40 border border-border-color rounded-lg"
                  data-testid={`cart-loose-line-${it.lineId}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary truncate" title={it.productName}>
                      {it.productName}
                      {it.size && <span className="text-text-secondary text-xs ml-1">({it.size})</span>}
                    </p>
                    {it.selectedFlavour && (
                      <p className="text-xs text-gold truncate">{it.selectedFlavour}</p>
                    )}
                    <p className="text-[11px] text-text-secondary">
                      ${unit.toFixed(2)} × {it.qty} = <span className="text-text-primary">${(unit * it.qty).toFixed(2)}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => onUpdateLooseQty(it.lineId, Math.max(0, it.qty - 1))}
                      disabled={busy}
                      data-testid={`cart-loose-decrement-${it.lineId}`}
                      className="w-6 h-6 rounded border border-border-color text-text-primary hover:border-gold disabled:opacity-30 text-sm"
                      aria-label="Decrease quantity"
                    >−</button>
                    <input
                      type="number"
                      min="1"
                      key={`${it.lineId}-${it.qty}`}
                      defaultValue={it.qty}
                      disabled={busy}
                      data-testid={`cart-loose-qty-${it.lineId}`}
                      aria-label="Quantity"
                      onBlur={(e) => {
                        const v = Math.min(999, Math.max(1, Math.floor(Number(e.target.value) || it.qty)));
                        if (v !== it.qty) onUpdateLooseQty(it.lineId, v);
                        else e.target.value = it.qty;
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      className="w-11 bg-white border border-gray-400 rounded text-center text-xs font-bold text-black py-0.5 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      onClick={() => onUpdateLooseQty(it.lineId, it.qty + 1)}
                      disabled={busy}
                      data-testid={`cart-loose-increment-${it.lineId}`}
                      className="w-6 h-6 rounded border border-border-color text-text-primary hover:border-gold disabled:opacity-30 text-sm"
                      aria-label="Increase quantity"
                    >+</button>
                    <button
                      onClick={() => onRemoveLooseItem(it.lineId)}
                      disabled={busy}
                      data-testid={`cart-loose-remove-${it.lineId}`}
                      className="ml-1 text-text-secondary hover:text-danger disabled:opacity-30"
                      aria-label="Remove line item"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-text-secondary mt-2 text-right">
            Loose subtotal: <span className="text-text-primary font-semibold">${looseSubtotal.toFixed(2)}</span>
          </p>
        </div>
      )}

      {!loading && savers.length > 0 && (
        <div className="mb-4">
          <p className="text-xs uppercase tracking-wider text-text-secondary mb-2">
            Savers ({savers.length})
          </p>
          <ul className="space-y-2" data-testid="cart-savers">
            {savers.map((b) => {
              const t = computeBundleTotals(b);
              return (
                <li
                  key={b.id}
                  className="flex items-start gap-2 p-2 bg-black/40 border border-border-color rounded-lg"
                  data-testid={`cart-saver-line-${b.id}`}
                >
                  <Package size={16} weight="duotone" className="text-gold mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary truncate" title={b.name}>
                      <span className="font-mono text-gold text-xs mr-1">#{String(b.number || 0).padStart(2, '0')}</span>
                      {b.name}
                      {b.source === 'planner' && (
                        <span
                          className="ml-1.5 inline-flex items-center px-1 py-0 text-[9px] uppercase tracking-wider bg-gold/15 text-gold border border-gold/30 rounded align-middle"
                          title="Created by Smart Saver Planner"
                          data-testid={`cart-saver-auto-badge-${b.id}`}
                        >
                          Auto
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-text-secondary">
                      Subtotal ${t.subtotal.toFixed(2)} · Saver Price <span className="text-gold font-semibold">${t.bundlePrice.toFixed(2)}</span>
                      {!isCustomer && t.customerSaving > 0 && (
                        <span className="ml-1 text-success">(save ${t.customerSaving.toFixed(2)})</span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => onRemoveSaver(b.id)}
                    disabled={busy}
                    data-testid={`cart-saver-remove-${b.id}`}
                    className="text-text-secondary hover:text-danger disabled:opacity-30 shrink-0"
                    aria-label="Remove Saver from cart"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-text-secondary mt-2 text-right">
            Savers total: <span className="text-text-primary font-semibold">${saversTotals.saverPrice.toFixed(2)}</span>
          </p>
        </div>
      )}

      {!isEmpty && (
        <>
          <div className="border-t border-border-color pt-3 mb-3" data-testid="cart-grand-total">
            {appliedBanked > 0 && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Subtotal</span>
                  <span className="text-text-primary">${grandTotal.toFixed(2)}</span>
                </div>
                <div
                  className="flex items-center justify-between text-sm mt-1"
                  data-testid="cart-banked-credit-applied"
                >
                  <span className="text-gold flex items-center gap-1">
                    Prepay credit applied
                    <span
                      className="text-[10px] text-text-secondary"
                      title={`$${rawBanked.toFixed(2)} banked${
                        rawBanked > appliedBanked
                          ? ` — $${(rawBanked - appliedBanked).toFixed(2)} carries to next order.`
                          : ''
                      }`}
                    >
                      (banked ${rawBanked.toFixed(2)})
                    </span>
                  </span>
                  <span className="text-gold font-semibold">−${appliedBanked.toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-text-secondary">
                {appliedBanked > 0 ? 'Pre-GST total' : 'Grand total'}
              </span>
              <span
                className="text-2xl font-bold font-heading text-gold"
                data-testid="cart-final-total"
              >
                ${finalTotal.toFixed(2)}
              </span>
            </div>
            <p className="text-[10px] text-text-secondary text-right">+ gst</p>
          </div>

          {!confirmingSubmit ? (
            <button
              onClick={() => setConfirmingSubmit(true)}
              disabled={busy}
              data-testid="cart-submit-button"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50"
            >
              Submit Order <ArrowRight size={16} weight="bold" />
            </button>
          ) : (
            <div className="space-y-2" data-testid="cart-submit-confirm">
              <p className="text-xs text-text-secondary">
                Send this combined order via email? The cart will be cleared on success.
              </p>
              <div className="px-3 py-2 bg-black/20 border border-border-color rounded-lg">
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold block mb-1" htmlFor="cart-customer-reference-input">
                  Your reference (optional)
                </label>
                <input
                  id="cart-customer-reference-input"
                  type="text"
                  value={customerReference}
                  onChange={(e) => setCustomerReference(e.target.value)}
                  disabled={busy}
                  placeholder="e.g. PO-12345 — appears on the Unleashed inventory file"
                  data-testid="cart-customer-reference-input"
                  maxLength={64}
                  className="w-full bg-transparent text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none"
                />
              </div>
              <label className="flex items-start gap-2 px-3 py-2 bg-black/20 border border-border-color rounded-lg cursor-pointer hover:border-gold/60">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  disabled={busy}
                  data-testid="cart-submit-accept-terms"
                  className="mt-0.5 accent-gold w-4 h-4 shrink-0"
                />
                <span className="text-xs text-text-primary leading-relaxed">
                  <TermsAcceptanceLabel />
                </span>
              </label>
              {prepayConfig.enabled && prepayConfig.label && (
                <label className="flex items-start gap-2 px-3 py-2 bg-black/20 border border-border-color rounded-lg cursor-pointer hover:border-gold/60">
                  <input
                    type="checkbox"
                    checked={prepayRequested}
                    onChange={(e) => setPrepayRequested(e.target.checked)}
                    disabled={busy}
                    data-testid="cart-submit-accept-prepay"
                    className="mt-0.5 accent-gold w-4 h-4 shrink-0"
                  />
                  <span className="text-xs text-text-secondary leading-relaxed">
                    {prepayConfig.label}
                  </span>
                </label>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmingSubmit(false)}
                  disabled={busy}
                  data-testid="cart-submit-cancel"
                  className="flex-1 px-3 py-2 border border-border-color text-text-primary text-sm rounded-lg hover:border-gold"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const ok = await onSubmit({ prepayRequested, customerReference });
                    if (ok) setConfirmingSubmit(false);
                  }}
                  disabled={busy || !acceptedTerms}
                  data-testid="cart-submit-confirm-button"
                  title={!acceptedTerms && !busy ? 'Tick the Terms of Trade checkbox to continue.' : undefined}
                  className="flex-1 px-3 py-2 bg-gold text-black text-sm font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? 'Sending…' : 'Confirm & Send'}
                </button>
              </div>
            </div>
          )}

          {submitStatus && (
            <p
              data-testid="cart-submit-status"
              className={`mt-3 text-xs px-2 py-1.5 rounded ${
                submitStatus.kind === 'ok'
                  ? 'text-success bg-success/10 border border-success/30'
                  : 'text-danger bg-danger/10 border border-danger/30'
              }`}
            >
              {submitStatus.text}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default CartPanel;
